import React, { useState } from 'react';
import { getStorePrice, getStorePriceMap } from '../../../../utils/tradeInvestment';

/** Derived rates for the analysis card. */
export function getVisitRates({ totalStores, uniqueStoresVisited, totalVisits }) {
    const visitRate = totalStores > 0 ? (uniqueStoresVisited / totalStores) * 100 : 0;
    const avgVisitsPerStore = uniqueStoresVisited > 0 ? (totalVisits / uniqueStoresVisited).toFixed(1) : 0;
    return { visitRate, avgVisitsPerStore };
}

/** Filas de "Resultados por Piso de Venta" y guardado del precio por piso. */
export function useCampaignStoreResults({ campaign, contracts, onStorePriceChange }) {
    const [savingStoreId, setSavingStoreId] = React.useState(null);

    const priceMap = getStorePriceMap(campaign);

    const handlePriceChange = async (storeId, price) => {
        if (!onStorePriceChange) return;
        try {
            setSavingStoreId(storeId);
            await onStorePriceChange(storeId, price);
        } finally {
            setSavingStoreId(null);
        }
    };

    // Create a map of store checks by store ID
    const storeCheckMap = new Map();
    contracts?.forEach((contract) => {
        storeCheckMap.set(contract.storeId, contract);
    });

    // Build data for all stores in campaign
    const storeData =
        campaign?.stores?.map((store) => {
            const check = storeCheckMap.get(store._id);

            let imageCount = 0;
            let images = [];

            if (check) {
                if (check.images?.length) {
                    check.images.forEach((img) => {
                        if (img.url) {
                            images.push({ url: img.url, type: 'general' });
                            imageCount++;
                        }
                    });
                }

                if (check.campaignContent?.length) {
                    check.campaignContent.forEach((content) => {
                        if (content.images?.length) {
                            content.images.forEach((img) => {
                                if (img.url) {
                                    images.push({ url: img.url, material: content.material, type: 'material' });
                                    imageCount++;
                                }
                            });
                        }
                    });
                }
            }

            return {
                key: store._id,
                storeId: store._id,
                storeName: store.name,
                clientName: store.clientName || 'N/A',
                storeType: store.storeType || 'N/A',
                visited: !!check,
                available: check?.available,
                imageCount,
                images,
                checkDate: check?.createdAt,
                price: getStorePrice(campaign, store._id, priceMap),
                hasCustomPrice: priceMap.has(String(store._id))
            };
        }) || [];

    const totalInvestment = storeData.reduce((total, row) => total + (row.price || 0), 0);

    return { storeData, totalInvestment, savingStoreId, handlePriceChange };
}

/** Imágenes de evidencia (generales y por material) con filtros por piso, material y búsqueda. */
export function useCampaignImageGallery({ campaign, contracts }) {
    const [filterStore, setFilterStore] = useState('all');
    const [filterMaterial, setFilterMaterial] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');

    // Collect all images with metadata
    const allImages = [];

    contracts?.forEach((contract) => {
        const store = campaign?.stores?.find((s) => s._id === contract.storeId);

        if (contract.images?.length) {
            contract.images.forEach((img, idx) => {
                if (img.url) {
                    allImages.push({
                        url: img.url,
                        storeId: contract.storeId,
                        storeName: store?.name || 'Desconocido',
                        clientName: store?.clientName || 'N/A',
                        type: 'general',
                        material: 'General',
                        date: contract.createdAt,
                        available: contract.available,
                        key: `${contract.storeId}-general-${idx}`
                    });
                }
            });
        }

        if (contract.campaignContent?.length) {
            contract.campaignContent.forEach((content) => {
                if (content.images?.length) {
                    content.images.forEach((img, idx) => {
                        if (img.url) {
                            allImages.push({
                                url: img.url,
                                storeId: contract.storeId,
                                storeName: store?.name || 'Desconocido',
                                clientName: store?.clientName || 'N/A',
                                type: 'material',
                                material: content.material || 'Sin especificar',
                                date: contract.createdAt,
                                available: contract.available,
                                key: `${contract.storeId}-${content.material}-${idx}`
                            });
                        }
                    });
                }
            });
        }
    });

    // Get unique stores and materials for filters
    const uniqueStores = [...new Set(allImages.map((img) => img.storeId))].map((storeId) => {
        const img = allImages.find((i) => i.storeId === storeId);
        return { id: storeId, name: img.storeName };
    });

    const uniqueMaterials = [...new Set(allImages.map((img) => img.material))];

    // Apply filters
    let filteredImages = allImages;

    if (filterStore !== 'all') {
        filteredImages = filteredImages.filter((img) => img.storeId === filterStore);
    }

    if (filterMaterial !== 'all') {
        filteredImages = filteredImages.filter((img) => img.material === filterMaterial);
    }

    if (searchTerm) {
        const term = searchTerm.toLowerCase();
        filteredImages = filteredImages.filter(
            (img) => img.storeName.toLowerCase().includes(term) || img.clientName.toLowerCase().includes(term) || img.material.toLowerCase().includes(term)
        );
    }

    // Sort by date (newest first)
    filteredImages.sort((a, b) => new Date(b.date) - new Date(a.date));

    return {
        allImages,
        filteredImages,
        uniqueStores,
        uniqueMaterials,
        filterStore,
        setFilterStore,
        filterMaterial,
        setFilterMaterial,
        searchTerm,
        setSearchTerm
    };
}
